/**
 * Contact Form 7 behaviour, applied to every form on the site.
 *
 * Its own bundle, loaded only on pages that actually carry a form
 * (inc/enqueue.php → gf_page_has_form). Four rules, all global:
 *
 *   1. The submit button starts disabled and unlocks the moment every required
 *      field — text, email, select, radio group, checkbox group, acceptance —
 *      is filled and valid. Validity is the browser's own `checkValidity()`, so
 *      a malformed email counts as unfilled without a second rule set here.
 *   2. While it is locked the form cannot be submitted at all: the click and the
 *      submit event are both stopped before the plugin sees them, so a stray
 *      Enter key or a script re-enabling the button changes nothing.
 *   3. One submission at a time. The first one locks the form until the plugin
 *      answers, which is what stops a double click from sending twice.
 *   4. The plugin's events become classes on the form (is-sending, is-sent,
 *      is-invalid, is-failed), so the states are styled in CSS rather than here.
 *
 * None of this replaces the plugin's server-side validation, and a form still
 * works with this script blocked.
 *
 * Phone fields get a country picker and per-country validation from
 * src/js/phone.ts, imported only when the page has one — see initPhoneFields().
 */

import { DURATION, EASE_OUT, reducedMotion } from './utils/motion';

// The plugin's own wrapper is stripped in inc/cleanup.php, so the state class
// goes on whatever element actually holds the control.
const WRAP = '.wpcf7-form-control-wrap, .gf-field';

// If the plugin never answers — a dropped request, a 500 — the form would stay
// locked for good. This is the way out.
const SENDING_TIMEOUT = 15000;

type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

type FormState = {
  ready: boolean;
  sending: boolean;
  refresh: () => void;
};

/** Per-form state, read by the document-level guards below. */
const states = new WeakMap<HTMLFormElement, FormState>();

/** Controls the visitor fills in, excluding the plugin's hidden bookkeeping. */
const fieldsOf = (form: HTMLFormElement): Field[] =>
  Array.from(form.querySelectorAll<Field>('input, select, textarea')).filter(
    (field) => !['hidden', 'submit', 'button'].includes((field as HTMLInputElement).type)
  );

/** A group is required when any of its inputs is flagged as such. */
const isRequired = (field: Field): boolean => {
  if (field.hasAttribute('aria-required') || field.hasAttribute('required')) {
    return true;
  }

  // Acceptance is required unless the shortcode marks it optional.
  const wrap = field.closest('.wpcf7-acceptance');

  return !!wrap && !wrap.classList.contains('optional');
};

/** Every input sharing a name — a checkbox or radio group answers as one. */
const group = (form: HTMLFormElement, field: Field): Field[] =>
  field.name
    ? Array.from(form.querySelectorAll<Field>(`[name="${CSS.escape(field.name)}"]`))
    : [field];

const isFilled = (form: HTMLFormElement, field: Field): boolean => {
  const type = (field as HTMLInputElement).type;

  if ('checkbox' === type || 'radio' === type) {
    return group(form, field).some((input) => (input as HTMLInputElement).checked);
  }

  if (!field.value.trim()) {
    return false;
  }

  // Type-specific formats (email, url, tel with a pattern) come for free.
  return field.checkValidity();
};

/** Are all required fields answered? */
const isComplete = (form: HTMLFormElement): boolean =>
  fieldsOf(form)
    .filter(isRequired)
    .every((field) => isFilled(form, field));

const setState = (form: HTMLFormElement, field: Field): void => {
  const wrap = field.closest<HTMLElement>(WRAP) || field.parentElement;

  if (!wrap || wrap === form) {
    return;
  }

  const filled = isFilled(form, field);

  wrap.classList.add('gf-field');
  wrap.classList.toggle('is-invalid', isRequired(field) && !filled);
  wrap.classList.toggle('is-valid', filled);
};

const setup = (form: HTMLFormElement): void => {
  const submit = form.querySelector<HTMLButtonElement | HTMLInputElement>('.wpcf7-submit');

  if (!submit) {
    return;
  }

  const required = fieldsOf(form).filter(isRequired);

  const state: FormState = {
    ready: false,
    sending: false,
    refresh: () => undefined,
  };

  state.refresh = () => {
    // A form with nothing required is never locked by validation.
    state.ready = !required.length || isComplete(form);

    const usable = state.ready && !state.sending;

    submit.disabled = !usable;
    submit.setAttribute('aria-disabled', String(!usable));
    submit.classList.toggle('is-disabled', !usable);
    form.classList.toggle('is-ready', state.ready);
  };

  states.set(form, state);

  // Touched fields get their state on every keystroke; untouched ones wait for
  // the first blur, so a half-typed email is not called wrong.
  form.addEventListener('input', (event) => {
    const field = event.target as Field;

    if (field.dataset && 'touched' in field.dataset) {
      setState(form, field);
    }

    state.refresh();
  });

  form.addEventListener('change', (event) => {
    const field = event.target as Field;

    field.dataset.touched = '1';
    setState(form, field);
    state.refresh();
  });

  form.addEventListener(
    'blur',
    (event) => {
      const field = event.target as Field;

      if (!field.name) {
        return;
      }

      field.dataset.touched = '1';
      setState(form, field);
    },
    true // blur does not bubble
  );

  const release = (): void => {
    state.sending = false;
    form.classList.remove('is-sending');
    state.refresh();
  };

  // The plugin's own lifecycle, turned into classes.
  form.addEventListener('wpcf7mailsent', () => {
    form.classList.add('is-sent');
    form.classList.remove('is-invalid', 'is-failed');
    showAnswer(form);
  });

  form.addEventListener('wpcf7invalid', () => {
    form.classList.add('is-invalid');

    // Send focus where the answer has to change.
    form.querySelector<HTMLElement>('.wpcf7-not-valid')?.focus();
  });

  form.addEventListener('wpcf7spam', () => form.classList.add('is-invalid'));
  form.addEventListener('wpcf7mailfailed', () => form.classList.add('is-failed'));

  // Fires after every submission, whatever the outcome: the form is free again.
  form.addEventListener('wpcf7submit', () => {
    window.setTimeout(() => {
      if (form.classList.contains('is-sent')) {
        // The plugin has just emptied the fields, so the button locks itself.
        form
          .querySelectorAll<HTMLElement>('.is-valid, .is-invalid')
          .forEach((wrap) => wrap.classList.remove('is-valid', 'is-invalid'));

        fieldsOf(form).forEach((field) => delete field.dataset.touched);
      }

      release();
    }, 0);
  });

  state.refresh();
};

/**
 * Put the block's answer where the form was.
 *
 * The markup is already on the page — the block renders it (blocks/contact) —
 * so nothing is built here. CSS swaps the two through the theme's popover rule;
 * the one thing it cannot do is know the height it is moving to, which is what
 * the animation below is for. A form without an answer panel keeps the plugin's
 * own message, which is why this leaves quietly when it finds none.
 */
const showAnswer = (form: HTMLFormElement): void => {
  const states = form.closest<HTMLElement>('[data-contact-states]');
  const answer = states?.querySelector<HTMLElement>('[data-contact-success]');

  if (!states || !answer) {
    return;
  }

  const from = states.getBoundingClientRect().height;

  states.classList.add('is-done');

  // Measured after the class: the card is out of the flow by now, so the box
  // has already collapsed to the answer's own height.
  const to = states.getBoundingClientRect().height;

  if (!reducedMotion() && Math.abs(to - from) > 1) {
    const animation = states.animate(
      { height: [`${from}px`, `${to}px`] },
      { duration: DURATION.base, easing: EASE_OUT }
    );

    animation.onfinish = () => {
      states.style.height = '';
    };
  }

  // The answer is a live region, so it is read out; focus follows it as well,
  // because the thing the visitor was working on has just gone.
  window.setTimeout(() => answer.focus({ preventScroll: true }), DURATION.fast);
};

const stateOf = (element: EventTarget | null): [HTMLFormElement, FormState] | null => {
  const form = element instanceof HTMLElement ? element.closest('form.wpcf7-form') : null;
  const state = form ? states.get(form as HTMLFormElement) : undefined;

  return form && state ? [form as HTMLFormElement, state] : null;
};

/**
 * Guards on the document, in the capture phase, so they run before the plugin's
 * own handlers on the form — by the time those would fire, the event is already
 * stopped.
 */
/**
 * The country picker, fetched once for the whole page.
 *
 * A dynamic import so the library and its number rules stay out of the form
 * bundle: a page with no phone field never downloads them, and a page with five
 * forms downloads them once — webpack hands every caller the same chunk.
 */
const initPhoneFields = (): void => {
  if (!document.querySelector('input[type="tel"]')) {
    return;
  }

  void import('./phone')
    .then((module) => module.initPhones())
    .catch(() => undefined);
};

/**
 * The multi-file drop zone, fetched once for the whole page.
 *
 * Same bargain as the country picker above: a page without the field never
 * downloads the module or its stylesheet.
 */
const initDropzones = (): void => {
  if (!document.querySelector('[data-gf-dropzone]')) {
    return;
  }

  void import('./uploads')
    .then((module) => module.initDropzones())
    .catch(() => undefined);
};

const guard = (): void => {
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;

      if (!(target instanceof HTMLElement) || !target.closest('.wpcf7-submit')) {
        return;
      }

      const found = stateOf(target);

      if (!found) {
        return;
      }

      const [, state] = found;

      if (!state.ready || state.sending) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const found = stateOf(event.target);

      if (!found) {
        return;
      }

      const [form, state] = found;

      // Incomplete, or a second submission while the first is in flight.
      if (!state.ready || state.sending) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      state.sending = true;
      form.classList.add('is-sending');
      form.classList.remove('is-sent', 'is-failed');
      state.refresh();

      // Nothing came back: let the visitor try again rather than stay stuck.
      window.setTimeout(() => {
        if (state.sending) {
          state.sending = false;
          form.classList.remove('is-sending');
          state.refresh();
        }
      }, SENDING_TIMEOUT);
    },
    true
  );
};

const init = (): void => {
  const forms = document.querySelectorAll<HTMLFormElement>('.wpcf7-form');

  if (!forms.length) {
    return;
  }

  forms.forEach(setup);
  guard();
  initPhoneFields();
  initDropzones();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export {};
